import { NoRecordFound } from 'src/pages/ReviewList/EmptyState';
import {
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Box,
  Tooltip
} from '@chakra-ui/react';
import { ButtonIcon, Modal, Pagination } from '@morressier/alembic';
import { IVendorHistoryResponse } from '@publishing-engine/domain/models/vendor-exports';
import { ReactNode, useState } from 'react';
import VendorExportHistoryDetail from './VendorExportHistoryDetail';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { formatDateAndTime } from '../../utils/date';

interface VendorExportHistoryTableProps {
  paginatedData: IVendorHistoryResponse;
  renderBadge: (status: string) => ReactNode;
  total?: number;
  totalPages?: number;
  page?: number;
  setPage?: (arg0: number) => void;
}

const VendorExportHistoryTable = ({
  paginatedData,
  renderBadge,
  total,
  totalPages,
  page,
  setPage
}: VendorExportHistoryTableProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<
    IVendorHistoryResponse['data'][number] | null
  >(null);

  return (
    <>
      {total && total > 0 ? (
        <Flex direction="column" gap={4}>
          <Box overflowX="auto" width="100%">
          <Table
            variant="simple"
            bg="white"
            sx={{
              '& th, & td': {
                px: 4,
                py: 3
              }
            }}
          >
            <Thead>
              <Tr
                data-testid="exports-table-header"
                sx={{
                  '& th': {
                    textTransform: 'none',
                    color: 'black',
                    background: 'white'
                  }
                }}
              >
                <Th ps={1}>
                  <Text textStyle="headingItem">Manuscript ID</Text>
                </Th>
                <Th ps={1}>
                  <Text textStyle="headingItem">Export Name</Text>
                </Th>
                <Th ps={1}>
                  <Text textStyle="headingItem">Configure Type</Text>
                </Th>
                <Th ps={1}>
                  <Text textStyle="headingItem">Status</Text>
                </Th>
                <Th ps={1}>
                  <Text textStyle="headingItem">Date</Text>
                </Th>
              </Tr>
            </Thead>

            <Tbody>
              {paginatedData.data.map((item, index) => {
                return (
                  <Tr
                    key={`${item.manuscriptId}-${index}`}
                    data-testid={`exports-table-row-${index}`}
                  >
                    <Td ps={1}>
                      <Text>{item.manuscriptId}</Text>
                    </Td>
                    <Td ps={1}>
                      <Flex direction="column">
                        <Text>{item.event.exportName}</Text>
                        <Text fontSize="sm">
                          {item.event.exportType === 'full'
                            ? 'Package file'
                            : 'Metadata Only'}
                        </Text>
                      </Flex>
                    </Td>
                    <Td ps={1}>
                      <Flex direction="column">
                        <Text>{item.event.exportConfigType.toUpperCase()}</Text>
                        <Text fontSize="sm">{item.event.triggeredBy}</Text>
                      </Flex>
                    </Td>
                    <Td ps={1}>{renderBadge(item.event.status)}</Td>
                    <Td ps={1}>
                      <Text>
                        {formatDateAndTime(item.date)}
                      </Text>
                    </Td>
                    <Td
                      ps={1}
                      onClick={() => {
                        setSelectedRow(item);
                        setIsOpen(true);
                      }}
                    >
                      <Tooltip
                        hasArrow
                        shouldWrapChildren
                        placement="top"
                        label={'View Details'}
                        borderRadius={4}
                        data-cy="contributions-info-tooltip"
                      >
                        <ButtonIcon
                          size="small"
                          ariaLabel="View Export History Details"
                          icon={<FontAwesomeIcon icon={faInfoCircle} />}
                          data-cy="contributions-info-icon"
                        />
                      </Tooltip>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
          </Box>
          {
            <Box mt={4} display="flex" justifyContent="center">
              {totalPages && page && setPage && (
                <Pagination
                  key={totalPages}
                  ariaLabel="Export history pagination"
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                />
              )}
            </Box>
          }
          {selectedRow && (
            <Modal
              isOpen={isOpen}
              onClose={() => {
                setIsOpen(false);
              }}
              width={{ base: 'calc(100vw - 16px)', md: '720px' } as any}
              closeButtonProps={{
                hidden: false
              }}
            >
              <VendorExportHistoryDetail selectedRow={selectedRow} />
            </Modal>
          )}
        </Flex>
      ) : (
        <NoRecordFound
          title="No export history"
          subTitle="No export history found for this submission"
        />
      )}
    </>
  );
};

export default VendorExportHistoryTable;
